import React, { useState } from 'react';
import './App.css';
import InputField from './InputField';
import MoneyInput from './MoneyInput';
import Login from './Login';
import AdminPanel from './AdminPanel';

const App = () => {
  const [user, setUser] = useState(null);
  const [bills, setBills] = useState({});
  const [invoices, setInvoices] = useState({});
  const [other, setOther] = useState(0);
  const [history, setHistory] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [theme, setTheme] = useState('blue');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState('new');
  const [isReadOnly, setIsReadOnly] = useState(false);

  const coins = [50, 100, 200, 500, 1000];
  const billDenoms = [1000, 2000, 5000, 10000, 20000, 50000, 100000];
  const invoiceNumbers = Array.from({length: 26}, (_, i) => i + 1);

  const nextField = (e) => {
    const inputs = Array.from(document.querySelectorAll('input'));
    const current = inputs.indexOf(e.target);
    
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      const next = inputs[current + 1];
      if (next) next.focus();
    } else if (e.key === 'ArrowUp') {
      const prev = inputs[current - 1];
      if (prev) prev.focus();
    } else if (e.key === 'ArrowRight') {
      const next = inputs[current + 1];
      if (next) next.focus();
    } else if (e.key === 'ArrowLeft') {
      const prev = inputs[current - 1];
      if (prev) prev.focus();
    }
  };

  const themes = {
    blue: { primary: '#4299e1', secondary: '#3182ce' },
    green: { primary: '#48bb78', secondary: '#38a169' },
    purple: { primary: '#9f7aea', secondary: '#805ad5' },
    red: { primary: '#f56565', secondary: '#e53e3e' }
  };

  const updateAmount = (denom, qty) => {
    setBills(prev => ({...prev, [denom]: qty || 0}));
  };

  const updateInvoice = (num, value) => {
    setInvoices(prev => ({...prev, [num]: value || 0}));
  };

  const coinTotal = coins.reduce((sum, denom) => 
    sum + (denom * (bills[denom] || 0)), 0);
  
  const billTotal = billDenoms.reduce((sum, denom) => 
    sum + (denom * (bills[denom] || 0)), 0);
  
  const invoiceTotal = Object.values(invoices).reduce((sum, val) => sum + val, 0);
  const grandTotal = coinTotal + billTotal + other;
  const change = grandTotal - invoiceTotal;

  const saveToHistory = async () => {
    if (grandTotal > 0) {
      try {
        await fetch('/api/registros', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            usuario_email: user.email,
            moneda_total: coinTotal,
            billete_total: billTotal,
            otro_valor: other,
            total_efectivo: grandTotal,
            total_facturas: invoiceTotal,
            cambio: change,
            denominaciones: bills,
            facturas: invoices
          })
        });
        loadHistory();
      } catch (error) {
        console.error('Error guardando registro:', error);
      }
    }
  };

  const loadHistory = async () => {
    if (!user || !user.email) {
      console.log('Usuario o email no definido:', user);
      return;
    }
    try {
      console.log('Cargando historial para:', user.email);
      const response = await fetch(`/api/registros/${user.email}`);
      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Data received:', data);
      setHistory(data);
    } catch (error) {
      console.error('Error cargando historial:', error);
    }
  };

  const clearAll = async () => {
    await saveToHistory();
    setBills({});
    setInvoices({});
    setOther(0);
    setSelectedRecord('new');
    setIsReadOnly(false);
    document.querySelectorAll('input').forEach(input => input.value = '');
    setShowModal(false);
  };

  const handleRecordSelect = (recordId) => {
    setSelectedRecord(recordId);
    
    if (recordId === 'new') {
      setBills({});
      setInvoices({});
      setOther(0);
      setIsReadOnly(false);
      document.querySelectorAll('input').forEach(input => input.value = '');
    } else {
      const record = history.find(r => r.id === parseInt(recordId));
      if (record) {
        setBills(record.denominaciones);
        setInvoices(record.facturas);
        setOther(record.otro_valor);
        setIsReadOnly(true);
        
        setTimeout(() => {
          Object.entries(record.denominaciones).forEach(([denom, qty]) => {
            const input = document.querySelector(`input[data-denom="${denom}"]`);
            if (input) input.value = qty;
          });
          Object.entries(record.facturas).forEach(([num, value]) => {
            const input = document.querySelector(`input[data-invoice="${num}"]`);
            if (input) input.value = value.toLocaleString();
          });
          const otherInput = document.querySelector('input[data-other="true"]');
          if (otherInput) otherInput.value = record.otro_valor.toLocaleString();
        }, 50);
      }
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  React.useEffect(() => {
    if (user) {
      loadHistory();
    }
  }, [user]);

  // Debug: agregar console.log para verificar
  React.useEffect(() => {
    console.log('History state:', history);
  }, [history]);

  const handleLogout = () => {
    setUser(null);
    setBills({});
    setInvoices({});
    setOther(0);
    setHistory([]);
  };

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className={`app theme-${theme}`} style={{'--primary': themes[theme].primary, '--secondary': themes[theme].secondary}}>
      <div className="header">
        <h1>CONTADOR DE EFECTIVO</h1>
        <div className="header-controls">
          <span className="user-info">Usuario: {user.email}</span>
          {user.role === 'admin' && (
            <button onClick={() => setShowAdminPanel(true)} className="admin-btn">
              Administrar
            </button>
          )}
          <select value={theme} onChange={e => setTheme(e.target.value)} className="theme-selector">
            <option value="blue">Azul</option>
            <option value="green">Verde</option>
            <option value="purple">Morado</option>
            <option value="red">Rojo</option>
          </select>
          <button onClick={handleLogout} className="logout-btn">Salir</button>
        </div>
      </div>
      
      <div className="record-selector">
        <select value={selectedRecord} onChange={e => handleRecordSelect(e.target.value)}>
          <option value="new">📝 Nuevo Registro</option>
          {history.map(record => (
            <option key={record.id} value={record.id}>
              📊 {new Date(record.fecha).toLocaleString()} - Total: {record.total_efectivo.toLocaleString()}
            </option>
          ))}
        </select>
        {isReadOnly && <span className="readonly-indicator">🔒 MODO CONSULTA</span>}
      </div>
      
      {change < 0 && (
        <div className="alert">
          ⚠️ ALERTA: El cambio es negativo ({change.toLocaleString()})
        </div>
      )}
      
      <div className="sections">
        <div className="section">
          <h2>EFECTIVO</h2>
          
          <div className="section-header">
            <h3>MONEDA</h3>
            <div className="header-total">{coinTotal.toLocaleString()}</div>
          </div>
          {coins.map(denom => (
            <div key={denom} className="row">
              <span>{denom.toLocaleString()}</span>
              <span>×</span>
              <InputField
                onChange={e => updateAmount(denom, +e.target.value)}
                onKeyDown={nextField}
                onFocus={e => setActiveField(e.target)}
                onBlur={() => setActiveField(null)}
                className={activeField && activeField === document.activeElement ? 'active-field' : ''}
                readOnly={isReadOnly}
                data-denom={denom}
              />
              <span>=</span>
              <span>{((bills[denom] || 0) * denom).toLocaleString()}</span>
            </div>
          ))}
          
          <div className="section-header">
            <h3>BILLETE</h3>
            <div className="header-total">{billTotal.toLocaleString()}</div>
          </div>
          {billDenoms.map(denom => (
            <div key={denom} className="row">
              <span>{denom.toLocaleString()}</span>
              <span>×</span>
              <InputField
                onChange={e => updateAmount(denom, +e.target.value)}
                onKeyDown={nextField}
                onFocus={e => setActiveField(e.target)}
                onBlur={() => setActiveField(null)}
                className={activeField && activeField === document.activeElement ? 'active-field' : ''}
                readOnly={isReadOnly}
                data-denom={denom}
              />
              <span>=</span>
              <span>{((bills[denom] || 0) * denom).toLocaleString()}</span>
            </div>
          ))}
          
          <div className="otro-valor">
            <span>OTRO VALOR:</span>
            <MoneyInput
              onChange={value => setOther(value)}
              onKeyDown={nextField}
              onFocus={e => setActiveField(e.target)}
              onBlur={() => setActiveField(null)}
              className={activeField && activeField === document.activeElement ? 'active-field' : ''}
              readOnly={isReadOnly}
              data-other="true"
            />
          </div>
          
          <div className="grand-total animated">{grandTotal.toLocaleString()}</div>
        </div>

        <div className="section">
          <h2>FACTURAS</h2>
          <div className="invoices">
            <div>
              {Array.from({length: 13}, (_, i) => i + 1).map(num => (
                <div key={num} className="invoice-row">
                  <span>{num}</span>
                  <MoneyInput
                    onChange={value => updateInvoice(num, value)}
                    onKeyDown={nextField}
                    onFocus={e => setActiveField(e.target)}
                    onBlur={() => setActiveField(null)}
                    className={activeField && activeField === document.activeElement ? 'active-field' : ''}
                    readOnly={isReadOnly}
                    data-invoice={num}
                  />
                </div>
              ))}
            </div>
            <div>
              {Array.from({length: 13}, (_, i) => i + 14).map(num => (
                <div key={num} className="invoice-row">
                  <span>{num}</span>
                  <MoneyInput
                    onChange={value => updateInvoice(num, value)}
                    onKeyDown={nextField}
                    onFocus={e => setActiveField(e.target)}
                    onBlur={() => setActiveField(null)}
                    className={activeField && activeField === document.activeElement ? 'active-field' : ''}
                    readOnly={isReadOnly}
                    data-invoice={num}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="section-header">
            <span>FACTURAS</span>
            <div className="header-total">{invoiceTotal.toLocaleString()}</div>
          </div>
          <div className={`section-header ${change < 0 ? 'negative' : ''}`}>
            <span>CAMBIO</span>
            <div className="header-total">{change.toLocaleString()}</div>
          </div>
          
          {!isReadOnly && (
            <button onClick={() => setShowModal(true)}>
              LIMPIAR
            </button>
          )}
        </div>
      </div>
      

      
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>¿Confirmar limpieza?</h3>
            <p>Se borrará toda la información actual</p>
            <div className="modal-buttons">
              <button onClick={clearAll} className="confirm-btn">Confirmar</button>
              <button onClick={() => setShowModal(false)} className="cancel-btn">Cancelar</button>
            </div>
          </div>
        </div>
      )}
      
      {showAdminPanel && (
        <AdminPanel 
          user={user} 
          onClose={() => setShowAdminPanel(false)} 
        />
      )}
    </div>
  );
};

export default App;